'use memo';

import { useEffect, useRef, useState } from 'react';
import { type AustralianAddress, useGetPropertySummary } from '@urbanx/agx.rpdata.client';
import {
  type ComparablePropertyModel as SearchNowComparablePropertyModel,
  type SearchNowStructuredAddress,
} from '@urbanx/agx.ui-components2';

const hasStructuredAddress = (property: SearchNowComparablePropertyModel) =>
  !!property.addressFirstLine &&
  !!property.addressSuburb &&
  !!property.addressState &&
  !!property.addressPostcode;

const canApplyMatchedAddress = (matchedAddress: AustralianAddress) =>
  !!matchedAddress.streetAddress &&
  !!matchedAddress.suburb &&
  !!matchedAddress.state &&
  !!matchedAddress.postalCode;

const toStructuredAddress = (
  matchedAddress: AustralianAddress
): SearchNowStructuredAddress | undefined => {
  if (!canApplyMatchedAddress(matchedAddress)) return undefined;

  const streetAddress = [matchedAddress.unitNumber, matchedAddress.streetAddress]
    .filter(Boolean)
    .join('/');

  return {
    addressFirstLine: streetAddress,
    addressSuburb: matchedAddress.suburb!,
    addressState: matchedAddress.state!,
    addressPostcode: matchedAddress.postalCode!,
  };
};

interface StructuredAddressRequest {
  property: SearchNowComparablePropertyModel;
  resolve: (address: SearchNowStructuredAddress | undefined) => void;
}

const useGetStructuredAddressForProperty = () => {
  const queuedRequests = useRef<StructuredAddressRequest[]>([]);
  const activeRequestRef = useRef<StructuredAddressRequest | null>(null);
  const checkedIds = useRef(new Set<number>());
  const [activeRequest, setActiveRequest] = useState<StructuredAddressRequest | null>(null);

  const { data, isPending, isError } = useGetPropertySummary(
    activeRequest?.property.address ?? '',
    activeRequest?.property.propertyId,
    !!activeRequest
  );

  const completeActiveRequest = (structuredAddress: SearchNowStructuredAddress | undefined) => {
    const completedPropertyId = activeRequestRef.current?.property.propertyId;
    if (completedPropertyId) checkedIds.current.add(completedPropertyId);

    activeRequestRef.current?.resolve(structuredAddress);

    const nextRequest = queuedRequests.current.shift() ?? null;
    activeRequestRef.current = nextRequest;
    setActiveRequest(nextRequest);
  };

  useEffect(() => {
    return () => {
      activeRequestRef.current?.resolve(undefined);
      queuedRequests.current.forEach((request) => request.resolve(undefined));
      queuedRequests.current = [];
    };
  }, []);

  useEffect(() => {
    if (!activeRequest || isPending) return;

    if (isError || !data?.matchedAddress) {
      completeActiveRequest(undefined);
      return;
    }

    completeActiveRequest(toStructuredAddress(data.matchedAddress));
  }, [activeRequest, data, isError, isPending]);

  return (
    property: SearchNowComparablePropertyModel
  ): Promise<SearchNowStructuredAddress | undefined> => {
    if (hasStructuredAddress(property) || checkedIds.current.has(property.propertyId)) {
      return Promise.resolve(undefined);
    }

    return new Promise((resolve) => {
      const request = { property, resolve };

      if (activeRequestRef.current) {
        queuedRequests.current.push(request);
        return;
      }

      activeRequestRef.current = request;
      setActiveRequest(request);
    });
  };
};

export default useGetStructuredAddressForProperty;
